import {ArrowLeft} from 'lucide-react'
import { Link } from 'react-router-dom'
import CurvePriceChart from '@/components/CurvePriceChart'
import LaunchHolders from '@/components/LaunchHolders'
import NewsFeed from '@/components/NewsFeed'
import ExplorerLink from '@/components/ExplorerLink'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import { tokenShareUrl } from '@/lib/shareSlug'
import SimTradeBox from '@/components/SimTradeBoxLazy'
import WatchButton from '@/components/WatchButton'
import ShareTokenLink from '@/components/ShareTokenLink'
import TokenContractAddress from '@/components/TokenContractAddress'
import TokenImageEditor from '@/components/TokenImageEditor'
import TokenPageBanner from '@/components/TokenPageBanner'
import TokenAboutEditor from '@/components/TokenAboutEditor'
import TokenSocialLinks from '@/components/TokenSocialLinks'
import TokenSocialLinksEditor from '@/components/TokenSocialLinksEditor'
import RecentTrades from '@/components/RecentTrades'
import { useEffect, useState } from 'react'
import type { Address } from 'viem'
import { useSimHolding, useSimLaunch, useSimLaunchpad } from '@/hooks/useSimLaunchpad'
import { isEthAddress } from '@/lib/launchpad/contracts'
import { hydrateOnChainLaunch } from '@/lib/launchpad/hydrate'
import { subscribeLaunchTraded } from '@/lib/launchpad/tradeEvents'
import { useProjectLinks, type ProjectLinkRecord } from '@/lib/projectLinks'
import { queueLaunchVerification } from '@/lib/verifyLaunch'
import {
  CREATOR_SHARE_BPS,
  CURVE_SUPPLY,
  LAUNCH_FEE_ETH,
  LP_SUPPLY,
  PLATFORM_SHARE_BPS,
  TOTAL_SUPPLY,
  TRADE_FEE_BPS,
} from '@/lib/launchpad/constants'
import { shortenAddress } from '@/lib/launchpad/contracts'
import { chartPointsForLaunch, isLaunchGraduated, launchGraduationQuoteEth } from '@/lib/launchpad/curveMath'
import { weiToEthString } from '@/lib/launchpad/wei'
import { formatBps } from '@/lib/protocol'

function SimLaunchDetail({ id }: { id: string }) {
  const launch = useSimLaunch(id)
  const holding = useSimHolding(id)
  const { walletWei } = useSimLaunchpad()
  const projectKeys = [launch?.contractAddress, launch?.shareSlug, launch?.id]
  const publishedLinks = useProjectLinks(
    projectKeys,
    launch ? { socials: launch.socials, image: launch.imageUrl } : undefined,
    'curve',
  )
  const [editedLinks, setEditedLinks] = useState<ProjectLinkRecord | null>(null)
  const links = {
    website: editedLinks?.website ?? publishedLinks.website,
    image: editedLinks?.image ?? publishedLinks.image ?? launch?.imageUrl,
    banner: editedLinks?.banner ?? publishedLinks.banner,
    about: editedLinks?.about ?? publishedLinks.about,
    socials: { ...publishedLinks.socials, ...editedLinks?.socials },
  }
  const defaultAbout = launch
    ? `On-chain bonding curve for ${launch.name} ($${launch.ticker}). Graduates at ${launchGraduationQuoteEth(launch)} ETH into LP.`
    : ''
  const chartPoints = launch ? chartPointsForLaunch(launch) : []
  const graduated = launch ? isLaunchGraduated(launch) : false
  const graduationEth = launch ? launchGraduationQuoteEth(launch) : '0'

  useEffect(() => {
    const address = (launch?.contractAddress && isEthAddress(launch.contractAddress))
      ? launch.contractAddress
      : (isEthAddress(id) ? id : undefined)
    if (!address)
      return
    const token = launch?.tokenAddress && launch.tokenAddress.toLowerCase() !== address.toLowerCase()
      ? launch.tokenAddress
      : undefined
    queueLaunchVerification({ kind: 'curve', address, token })

    let cancelled = false
    let retryTimer = 0
    const refresh = () => {
      if (cancelled)
        return
      void hydrateOnChainLaunch(address as Address, { force: true }).catch(() => undefined)
    }
    void hydrateOnChainLaunch(address as Address, { verify: true, force: true }).catch(() => undefined)

    const onVisible = () => {
      if (document.visibilityState === 'visible')
        refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onVisible)
    window.addEventListener('focus', onVisible)

    const unsubscribe = subscribeLaunchTraded((traded) => {
      if (cancelled)
        return
      const keys = [traded.id, traded.contractAddress, traded.tokenAddress]
        .filter((value): value is string => Boolean(value))
        .map(value => value.toLowerCase())
      if (!keys.includes(address.toLowerCase()) && !keys.includes(id.toLowerCase()))
        return
      refresh()
      window.clearTimeout(retryTimer)
      retryTimer = window.setTimeout(() => {
        if (!cancelled)
          refresh()
      }, 1500)
    })

    return () => {
      cancelled = true
      window.clearTimeout(retryTimer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onVisible)
      window.removeEventListener('focus', onVisible)
      unsubscribe()
    }
  }, [id, launch?.contractAddress, launch?.tokenAddress])

  if (!launch) {
    return (
      <Layout>
        <SeoHead title="Launch not found" description="This Penguin Pad launch could not be found." />
        <div className="mx-auto max-w-lg px-4 py-24 text-center">
          <h1 className="text-xl font-bold text-white">Launch not found</h1>
          <p className="mt-2 text-sm text-zinc-400">This curve listing is not in this browser.</p>
          <Link to="/launchpad" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to Launchpad
          </Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <SeoHead
        title={`${launch.name} (${launch.ticker})`}
        description={`On-chain bonding curve for ${launch.name}. Graduates at ${graduationEth} ETH into LP.`}
        image={links.banner}
        imageAlt={`${launch.name} ($${launch.ticker}) banner`}
        url={tokenShareUrl(launch.shareSlug, launch.id)}
      />
      <section className="mx-auto max-w-7xl px-4 pb-2 pt-10 sm:px-6 lg:px-8">
        <Link to="/launchpad" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Launchpad
        </Link>
        {graduated
          ? (
              <div className="mt-5 rounded-3xl border border-lime-400/40 bg-lime-400 px-6 py-5 text-black">
                <p className="text-3xl font-bold uppercase tracking-[0.18em] sm:text-5xl">Graduated</p>
                <p className="mt-2 text-sm font-medium sm:text-base">This curve hit {graduationEth} ETH and moved into LP.</p>
              </div>
            )
          : null}
        <TokenPageBanner
          name={launch.name}
          banner={links.banner}
          projectKeys={projectKeys}
          kind="curve"
          creatorAddress={launch.creatorAddress}
          accent="lime"
          onPublished={banner => setEditedLinks(prev => ({ ...prev, banner, image: prev?.image, about: prev?.about }))}
        />
        <div className="mt-5 flex items-start gap-4">
          <div className="relative h-14 w-14 shrink-0">
            {links.image
              ? (
                  <img src={links.image} alt={`${launch.name} token art`} className="h-14 w-14 rounded-2xl object-cover" />
                )
              : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-lime-400/30 bg-lime-400/10 font-bold text-lime-400">
                    {launch.ticker.slice(0, 2)}
                  </div>
                )}
            <TokenImageEditor
              projectKeys={projectKeys}
              kind="curve"
              creatorAddress={launch.creatorAddress}
              currentImage={links.image}
              accent="lime"
              onPublished={image => setEditedLinks(prev => ({ ...prev, image, banner: prev?.banner, about: prev?.about }))}
            />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-white sm:text-3xl">{launch.name}</h1>
              <ShareTokenLink shareSlug={launch.shareSlug} fallbackId={launch.id} accent="lime" />
              <WatchButton projectId={launch.id} projectName={launch.name} />
            </div>
            <div className="mt-2">
              <TokenContractAddress address={launch.tokenAddress || launch.contractAddress} accent="lime" />
            </div>
            <p className={`mt-3 inline-flex rounded-full px-5 py-2 text-base font-bold uppercase tracking-[0.22em] sm:text-lg ${
              graduated ? 'bg-lime-400 text-black' : 'bg-lime-400/15 text-lime-400'
            }`}
            >
              {graduated ? 'Graduated' : 'On curve'}
            </p>
            <p className="mt-3 text-sm text-zinc-400">
              $
              {launch.ticker}
              {' '}
              · on-chain bonding curve ·
              {' '}
              {graduated ? `graduated at ${graduationEth} ETH into LP` : `graduates at ${graduationEth} ETH`}
              {' '}
              · creator
              {' '}
              {shortenAddress(launch.creatorAddress)}
            </p>
            {launch.contractAddress
              ? (
                  <div className="mt-2">
                    <ExplorerLink address={launch.contractAddress} label="View contract on Blockscout" />
                  </div>
                )
              : null}
          </div>
        </div>
        <div className="mt-6">
          <TokenSocialLinksEditor
            projectKeys={projectKeys}
            kind="curve"
            creatorAddress={launch.creatorAddress}
            initial={links}
            accent="lime"
            onPublished={record => setEditedLinks(prev => ({ ...prev, ...record, image: record.image || prev?.image, banner: record.banner || prev?.banner, about: record.about || prev?.about }))}
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <h2 className="text-sm font-semibold text-white">About</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-400">
                {links.about || defaultAbout}
              </p>
              <TokenSocialLinks name={launch.name} socials={links.socials} website={links.website} accent="lime" embedded />
            </div>
            <TokenAboutEditor
              projectKeys={projectKeys}
              kind="curve"
              creatorAddress={launch.creatorAddress}
              initial={links}
              fallback={defaultAbout}
              accent="lime"
              onPublished={record => setEditedLinks(prev => ({ ...prev, ...record, image: record.image || prev?.image, banner: record.banner || prev?.banner, about: record.about || prev?.about }))}
            />
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <CurvePriceChart points={chartPoints} height={220} unit="eth" showGraduation />
              {chartPoints.length <= 1
                ? <p className="text-sm text-zinc-500">No trades yet. The first fill draws this curve.</p>
                : <p className="mt-3 text-[11px] text-zinc-500">ETH spot path from fills, starting at zero. Dashed marker is graduation into LP.</p>}
            </div>
            <SimTradeBox launch={launch} holding={holding} walletWei={walletWei} />
            <RecentTrades projectKey={launch.contractAddress || launch.id} kind="curve" accent="lime" />
            <LaunchHolders launch={launch} />
            {launch.contractAddress
              ? (
                  <NewsFeed
                    projectKey={launch.contractAddress}
                    kind="curve"
                    tokenAddress={launch.contractAddress}
                    accent="lime"
                  />
                )
              : null}
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
              <h2 className="text-sm font-semibold text-white">Tokenomics</h2>
              <p className="mt-1 text-xs text-zinc-500">1B supply. 800M sells on the curve. 200M seeds LP at {graduationEth} ETH.</p>
              <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-zinc-900" aria-hidden="true">
                <div className="bg-lime-400" style={{ width: '80%' }} />
                <div className="bg-lime-300" style={{ width: '20%' }} />
              </div>
              <ul className="mt-4 space-y-3">
                <li className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-lime-400" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-medium text-white">Bonding curve</p>
                      <p className="text-xs text-zinc-500">Sold until net quote hits {graduationEth} ETH</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-zinc-300">80%</span>
                </li>
                <li className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-lime-300" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-medium text-white">LP reserve</p>
                      <p className="text-xs text-zinc-500">Reserved LP tokens plus leftover curve inventory</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-zinc-300">20%</span>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-sm text-zinc-400 backdrop-blur-md">
              <h2 className="text-sm font-semibold text-white">Frozen launch params</h2>
              <ul className="mt-3 space-y-1.5 text-xs">
                <li>
                  Launch fee
                  {' '}
                  {LAUNCH_FEE_ETH}
                  {' '}
                  ETH (not curve reserves)
                </li>
                <li>
                  Trade fee
                  {' '}
                  0% (no per-trade fee on the curve)
                </li>
                <li>
                  At bonding (
                  {graduationEth}
                  {' '}
                  ETH):
                  {' '}
                  {formatBps(TRADE_FEE_BPS)}
                  {' '}
                  of raised ETH · creator
                  {' '}
                  {formatBps(CREATOR_SHARE_BPS)}
                  {' '}
                  / platform
                  {' '}
                  {formatBps(PLATFORM_SHARE_BPS)}
                </li>
                <li>
                  Supply
                  {' '}
                  {weiToEthString(TOTAL_SUPPLY, 0)}
                  {' '}
                  · curve
                  {' '}
                  {weiToEthString(CURVE_SUPPLY, 0)}
                  {' '}
                  · LP reserve
                  {' '}
                  {weiToEthString(LP_SUPPLY, 0)}
                </li>
              </ul>
            </div>
          </div>
          <div className="space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-xs leading-relaxed text-zinc-500 backdrop-blur-md">
              Fees come off the quote leg before reserves move. Curve creator fees are disbursed at bonding; after listing, Uniswap pays the same 75/25 split on every swap. Anti-snipe tax decays over 5 seconds on buys only. Create/buy/sell require a Robinhood Chain wallet.
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default SimLaunchDetail
